import { useEffect, useRef } from 'react';
import clsx from 'clsx';

interface Props {
  length?: number;
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
}

// Шесть ячеек под 6-значный код. При полном вводе вызывает onComplete.
// Поддерживает вставку (paste) и Backspace с откатом на предыдущую ячейку.
export default function CodeInput({
  length = 6,
  value,
  onChange,
  onComplete,
  autoFocus,
  disabled,
  error,
}: Props) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const chars = value.padEnd(length, ' ').slice(0, length).split('');

  const setAt = (idx: number, digit: string) => {
    const next = value.split('');
    next[idx] = digit;
    // чистим хвост пробелов
    const joined = next.join('').replace(/\s+$/g, '');
    onChange(joined);
    if (joined.length === length && onComplete) onComplete(joined);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) return;

    if (raw.length > 1) {
      // вставка
      const digits = raw.slice(0, length - idx).split('');
      const next = value.split('');
      for (let i = 0; i < digits.length; i++) next[idx + i] = digits[i]!;
      const joined = next.join('').replace(/\s+$/g, '');
      onChange(joined);
      const focusIdx = Math.min(idx + digits.length, length - 1);
      refs.current[focusIdx]?.focus();
      if (joined.length === length && onComplete) onComplete(joined);
      return;
    }

    setAt(idx, raw);
    if (idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Backspace') {
      if (chars[idx] !== ' ' && chars[idx] !== undefined && value[idx]) {
        setAt(idx, ' ');
      } else if (idx > 0) {
        refs.current[idx - 1]?.focus();
        setAt(idx - 1, ' ');
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < length - 1) {
      refs.current[idx + 1]?.focus();
    }
  };

  return (
    <div className="flex items-center gap-2 sm:gap-3">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={length}
          disabled={disabled}
          value={value[i] ?? ''}
          onChange={(e) => handleInput(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onFocus={(e) => e.currentTarget.select()}
          className={clsx(
            'h-14 w-10 sm:h-16 sm:w-12 border bg-white text-center text-2xl font-medium tracking-tight text-ink outline-none transition-colors',
            error
              ? 'border-orange'
              : 'border-line focus:border-ink',
            disabled && 'opacity-60',
          )}
        />
      ))}
    </div>
  );
}
